import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Calendar, ArrowRight, Tag } from "lucide-react";
import { format } from "date-fns";

const BLOG_IMAGE = "https://images.pexels.com/photos/7723603/pexels-photo-7723603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const categories = ["all", "news", "events", "awareness", "community", "health"];

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setBlogs(response.data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          blog.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = category === "all" || blog.category === category;
    return matchesSearch && matchesCategory;
  });

  const featuredBlog = filteredBlogs.find(b => b.featured) || filteredBlogs[0];
  const regularBlogs = filteredBlogs.filter(b => b !== featuredBlog);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="gradient-hero py-16" data-testid="blog-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">News & Updates</span>
            <h1 className="text-heading-1 text-white mt-4 mb-4">Stay Informed</h1>
            <p className="text-white/80 text-lg">
              Read the latest news, updates, and stories from Blue vs Blue.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-slate-200" data-testid="blog-filters">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="blog-search"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={category === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategory(cat)}
                  className={category === cat ? "btn-primary" : ""}
                  data-testid={`category-${cat}`}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Listing */}
      <section className="section-spacing" data-testid="blog-listing">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-slate-100 rounded-lg h-80 animate-pulse"></div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <>
              {/* Featured Article */}
              {featuredBlog && (
                <Link 
                  to={`/blog/${featuredBlog.blog_id}`}
                  className="block mb-12"
                  data-testid="featured-blog"
                >
                  <div className="grid md:grid-cols-2 gap-8 bg-slate-50 rounded-lg overflow-hidden group">
                    <div className="aspect-video md:aspect-auto md:h-full overflow-hidden">
                      <img 
                        src={featuredBlog.image_url || BLOG_IMAGE}
                        alt={featuredBlog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <span className="inline-block bg-awareness-blue text-deep-blue text-xs font-semibold px-3 py-1 rounded-full mb-4 w-fit uppercase">
                        Featured
                      </span>
                      <h2 className="text-heading-2 text-deep-blue mb-4 group-hover:text-awareness-blue transition-colors">
                        {featuredBlog.title}
                      </h2>
                      <p className="text-slate-600 mb-4 line-clamp-3">
                        {featuredBlog.excerpt || featuredBlog.content.substring(0, 200)}...
                      </p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(featuredBlog.created_at), "MMMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {featuredBlog.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Regular Articles */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularBlogs.map(blog => (
                  <Link 
                    key={blog.blog_id}
                    to={`/blog/${blog.blog_id}`}
                    className="card-event"
                    data-testid={`blog-card-${blog.blog_id}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={blog.media?.[0]?.url || blog.image_url || BLOG_IMAGE}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-awareness-blue text-xs font-semibold uppercase">
                          {blog.category}
                        </span>
                        <span className="text-slate-400 text-xs">
                          {format(new Date(blog.created_at), "MMM d, yyyy")}
                        </span>
                      </div>
                      <h3 className="text-heading-3 text-deep-blue mb-2 group-hover:text-awareness-blue transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {blog.excerpt || blog.content.substring(0, 150)}
                      </p>
                      <div className="mt-4 flex items-center text-deep-blue font-semibold text-sm">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16" data-testid="no-blogs">
              <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Articles Found</h3>
              <p className="text-slate-500">Check back soon for new content!</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
